'use client';

import { c as _c } from "react/compiler-runtime";
import useProducts from '@shared/utils/hooks/useProducts';
import useUser from '@shared/utils/hooks/useUser';
import { Apple, BarChart3, CheckCircle, ChevronDown, ChevronLeft, ChevronRight, Clock, Eye, FileText, Globe, Handshake, Heart, Hourglass, LinkIcon, Lock, MapPin, Monitor, Play, PlayCircle, ShieldCheck, Sparkles, Tag, Target, Users } from 'lucide-react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React, { useRef, useState } from 'react';
import { client, type RouterOutputs } from '../../utils/trpc';
import { useAuthModals } from '../../widgets/WidgetsContext';
import Button from '../common/Button';
import ProductImage from '../products/ProductImage';
import { PartnersCarousel } from '../UI/PartnersCarousel';
import AboutHeader from './AboutHeader';
import { faqs } from './faqData';
import MarketingFooter from './MarketingFooter';
import ScrollAnimations from './ScrollAnimations';

// Lazy-load below-fold sections to shrink the landing-page bundle.
// FeatureRequests fetches via React Query (no SSR data to preserve).
const FeatureRequests = dynamic(() => import('./FeatureRequests').then(mod => mod.FeatureRequests), {
  ssr: false
});
// Modals: only rendered after interaction — defer the bundle.
// PurchaseModal pulls @stripe/react-stripe-js + stripe-js/pure. AppWidgets
// already defers it for exactly that reason; importing it statically here put
// Stripe straight back onto the anonymous landing page it was removed from.
const PurchaseModal = dynamic(() => import('../common/PurchaseModal'), {
  ssr: false
});
const ContactFormModal = dynamic(() => import('./ContactFormModal'), {
  ssr: false
});
const DesktopDownloadModal = dynamic(() => import('./DesktopDownloadModal'), {
  ssr: false
});
interface Feature {
  id: string;
  title: string;
  description: string;
  videoSrc: string;
  backgroundImage?: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const features: Feature[] = [{
  id: '1',
  title: 'Map-Based Post Filtering',
  description: 'Filter posts by location with precise geospatial data. Find relevant information based on where you are or where events happen.',
  videoSrc: '/about/map.webp',
  backgroundImage: '/about/map.webp',
  icon: MapPin
}, {
  id: '2',
  title: 'Versatile Tagging System',
  description: 'Pair all forms of media to make factiis. Our "For Sale" factiii example shows how you can combine media, location, and data requirements.',
  videoSrc: '/about/factiiis.webp',
  backgroundImage: '/about/factiiis.webp',
  icon: Tag
}, {
  id: '3',
  title: 'Usage Analysis',
  description: 'Optional local engagement tracker using local database that keeps your analytics entirely on your device.',
  videoSrc: '/about/analytics.webp',
  backgroundImage: '/about/analytics.webp',
  icon: BarChart3
}, {
  id: '4',
  title: 'Private Posts',
  description: 'Posts that stay entirely on your end. Can be used to collect data, notes, and personal information without any server involvement.',
  videoSrc: '/about/private_posts.webp',
  backgroundImage: '/about/private_posts.webp',
  icon: Lock
}, {
  id: '5',
  title: 'Encrypted Vault',
  description: 'Store your 2FA codes, database backups, and imported authenticator keys — all encrypted on your device. Sync across devices with cloud backup or transfer via QR code.',
  videoSrc: '/about/vault.webp',
  backgroundImage: '/about/vault.webp',
  icon: ShieldCheck
}];

// Benefits data
interface Benefit {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const benefits: Benefit[] = [{
  id: '1',
  title: 'Verify Information',
  description: 'Source your claims with citations and collaborate with the community to fact-check content.',
  icon: CheckCircle
}, {
  id: '2',
  title: 'Unique Tagging',
  description: 'Build custom factiiis to label posts in 1000+ ways and make content more meaningful.',
  icon: Tag
}, {
  id: '3',
  title: 'Topic-Based Spaces',
  description: 'Join or create spaces for your interests and collaborate with like-minded people.',
  icon: Users
}, {
  id: '4',
  title: 'Privacy-First',
  description: 'Your data stays on your device. You control what to share and what stays private.',
  icon: ShieldCheck
}, {
  id: '5',
  title: 'Map-Based Posts',
  description: 'Filter posts by location with precise geospatial data for relevant local information.',
  icon: MapPin
}, {
  id: '6',
  title: 'Usage Analytics',
  description: 'Optional local analytics that track your engagement and never leave your device.',
  icon: BarChart3
}];

// Team vision data
interface TeamVision {
  id: string;
  name: string;
  role: string;
  content: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const teamVisions: TeamVision[] = [{
  id: '1',
  name: 'On Truth',
  role: 'Our Mission',
  content: 'We believe everyone deserves access to verified, unbiased information. Factiii exists to make truth easy to find and impossible to bury.',
  icon: Eye
}, {
  id: '2',
  name: 'On Privacy',
  role: 'Our Promise',
  content: 'Your data is yours. We built Factiii local-first because we believe privacy is a right, not a feature you pay for.',
  icon: ShieldCheck
}, {
  id: '3',
  name: 'On Community',
  role: 'Our Vision',
  content: 'The best fact-checking happens when people work together. We are building tools that empower communities to hold information accountable.',
  icon: Heart
}];

// FAQ data lives in faqData.ts so the server-rendered FAQPage JSON-LD on the
// homepage stays in sync with this accordion.

// Feature image with skeleton shimmer and fade-in on load
const FeatureImage = t0 => {
  const $ = _c(12);
  const {
    src,
    alt
  } = t0;
  const [loaded, setLoaded] = useState(false);
  let t1;
  if ($[0] !== loaded) {
    t1 = !loaded && <div className="absolute inset-0 overflow-hidden bg-slate-200/60 dark:bg-slate-700/40"><div className="absolute inset-0 animate-shimmer-sweep bg-gradient-to-r from-transparent via-white/50 to-transparent" /></div>;
    $[0] = loaded;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `relative size-full transition-opacity duration-300 ease-out ${loaded ? "opacity-100" : "opacity-0"}`;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setLoaded(true);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== alt || $[4] !== src) {
    t4 = <Image src={src} alt={alt} fill={true} loading="lazy" className="object-cover" onLoad={t3} />;
    $[3] = alt;
    $[4] = src;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <div className={t2}>{t4}</div>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t5) {
    t6 = <div className="relative aspect-video">{t1}{t5}</div>;
    $[9] = t1;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
type AboutPageProps = {
  statsData?: RouterOutputs['stats']['aboutStats'] | null;
  // Server-rendered "Explore" hub (links into factiiis/spaces/posts), passed as
  // a slot so its SSR HTML stays crawlable even though About is a client page.
  exploreSlot?: React.ReactNode;
};
export const AboutPage = t0 => {
  const $ = _c(141);
  const {
    statsData,
    exploreSlot
  } = t0;
  const {
    currentUser
  } = useUser();
  const {
    products
  } = useProducts(client);
  const router = useRouter();
  const {
    openSignupModal,
    openAuthContinueModal
  } = useAuthModals();
  const [desktopDownloadOpen, setDesktopDownloadOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [openFaqId, setOpenFaqId] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [foundersModalOpen, setFoundersModalOpen] = useState(false);
  const scrollContainerRef = useRef(null);
  const stats = {
    users: statsData?.userCount || 0,
    factiiis: statsData?.postFactiiiCount || 0,
    posts: statsData?.postCount || 0,
    partners: 3
  };
  let foundersToken;
  let t1;
  if ($[0] !== products) {
    const tronProducts = products.filter(_temp);
    foundersToken = products.find(_temp2);
    t1 = tronProducts.filter(_temp3).sort(_temp4);
    $[0] = products;
    $[1] = foundersToken;
    $[2] = t1;
  } else {
    foundersToken = $[1];
    t1 = $[2];
  }
  const purchasableTrons = t1;
  const leftTron = purchasableTrons[0];
  const rightTron = purchasableTrons.length > 1 ? purchasableTrons[purchasableTrons.length - 1] : undefined;
  let goToNext;
  let goToPrevious;
  let scrollToIndex;
  let t2;
  if ($[3] !== currentIndex) {
    goToNext = () => {
      const nextIndex = (currentIndex + 1) % features.length;
      setCurrentIndex(nextIndex);
      scrollToIndex(nextIndex);
    };
    goToPrevious = () => {
      const prevIndex = currentIndex === 0 ? features.length - 1 : currentIndex - 1;
      setCurrentIndex(prevIndex);
      scrollToIndex(prevIndex);
    };
    scrollToIndex = index => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const children = Array.from(container.querySelectorAll("[data-feature-card=\"true\"]"));
      const target = children[index];
      if (!target) {
        return;
      }
      const targetCenter = (target as HTMLElement).offsetLeft + (target as HTMLElement).offsetWidth / 2;
      const containerCenter = container.offsetWidth / 2;
      container.scrollTo({
        left: Math.max(0, targetCenter - containerCenter),
        behavior: "smooth"
      });
    };
    t2 = () => {
      const container_0 = scrollContainerRef.current;
      if (!container_0) {
        return;
      }
      const containerCenter_0 = container_0.scrollLeft + container_0.offsetWidth / 2;
      const children_0 = Array.from(container_0.querySelectorAll("[data-feature-card=\"true\"]"));
      if (children_0.length === 0) {
        return;
      }
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      for (let i = 0; i < children_0.length; i++) {
        const el = children_0[i];
        const elCenter = (el as HTMLElement).offsetLeft + (el as HTMLElement).offsetWidth / 2;
        const distance = Math.abs(elCenter - containerCenter_0);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      }
      if (closestIndex !== currentIndex) {
        setCurrentIndex(closestIndex);
      }
    };
    $[3] = currentIndex;
    $[4] = goToNext;
    $[5] = goToPrevious;
    $[6] = scrollToIndex;
    $[7] = t2;
  } else {
    goToNext = $[4];
    goToPrevious = $[5];
    scrollToIndex = $[6];
    t2 = $[7];
  }
  const handleManualScroll = t2;
  let t3;
  if ($[8] !== scrollToIndex) {
    t3 = () => {
      const id = requestAnimationFrame(() => {
        scrollToIndex(0);
      });
      return () => cancelAnimationFrame(id);
    };
    $[8] = scrollToIndex;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  React.useEffect(t3, t4);
  let t5;
  if ($[11] !== currentIndex || $[12] !== scrollToIndex) {
    t5 = () => {
      const onResize = () => {
        scrollToIndex(currentIndex);
      };
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    };
    $[11] = currentIndex;
    $[12] = scrollToIndex;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== currentIndex) {
    t6 = [currentIndex];
    $[14] = currentIndex;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  React.useEffect(t5, t6);
  let t7;
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ScrollAnimations />;
    t8 = <AboutHeader />;
    $[16] = t7;
    $[17] = t8;
  } else {
    t7 = $[16];
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = statsData && <div className="mb-6 flex items-center gap-3"><div className="flex -space-x-2"><div className="size-6 rounded-full border-2 border-background-primary bg-primary" /><div className="size-6 rounded-full border-2 border-background-primary bg-primary" /><div className="size-6 rounded-full border-2 border-background-primary bg-primary" /></div><span className="text-sm font-emphasis text-textSecondary">{stats.users.toLocaleString()}+ Community Members,{" "}{stats.factiiis.toLocaleString()}+ Factiiis Posted,{" "}{stats.posts.toLocaleString()}+ Posts Shared</span></div>;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <h1 className="text-3xl font-extrabold leading-tight text-textPrimary sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl">The Internet's<br /><span className="text-primary">Truth Layer</span></h1>;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <p className="mt-4 text-lg leading-relaxed text-textSecondary sm:text-xl md:text-2xl">Discover, verify, and share facts — while keeping your privacy.<br /><span className="text-primary">Humanity first. Privacy by default. Radical transparency.</span></p>;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== currentUser?.isAuthenticated || $[22] !== openSignupModal || $[23] !== router) {
    t13 = currentUser?.isAuthenticated ? () => router.push("/home") : openSignupModal;
    $[21] = currentUser?.isAuthenticated;
    $[22] = openSignupModal;
    $[23] = router;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = currentUser?.isAuthenticated ? "Go Home" : "Join the Community";
  let t15;
  if ($[25] !== t13 || $[26] !== t14) {
    t15 = <Button onClick={t13} variant="PRIMARY" text={t14} classNames="px-6 py-3 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200 sm:px-8 sm:py-4 sm:text-lg md:px-10 md:py-5" />;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setDesktopDownloadOpen(true);
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <button type="button" onClick={t16} className="inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold text-textSecondary transition-colors hover:text-textPrimary"><Monitor className="size-4" />Download for Desktop</button>;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t15) {
    t18 = <div className="mt-8 flex items-center gap-4 sm:mt-10">{t15}{t17}</div>;
    $[30] = t15;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Apple className="size-5" />;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <a href="https://apps.apple.com/app/id6445829602" target="_blank" rel="noopener noreferrer" aria-label={"Download on the App Store \u2014 Factiii"} className="inline-flex items-center gap-2 rounded-lg border border-borderPrimary bg-background-secondary px-4 py-2.5 text-sm font-semibold text-textPrimary transition-colors hover:bg-background-light hover:text-primary">{t19}<span className="flex flex-col leading-tight"><span className="text-[10px] font-normal text-textSecondary">Download on the</span>App Store</span></a>;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Play className="size-5" />;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="mt-6 flex flex-wrap items-center gap-3">{t20}<a href="https://play.google.com/store/apps/details?id=com.factiii" target="_blank" rel="noopener noreferrer" aria-label={"Get it on Google Play \u2014 Factiii"} className="inline-flex items-center gap-2 rounded-lg border border-borderPrimary bg-background-secondary px-4 py-2.5 text-sm font-semibold text-textPrimary transition-colors hover:bg-background-light hover:text-primary">{t21}<span className="flex flex-col leading-tight"><span className="text-[10px] font-normal text-textSecondary">Get it on</span>Google Play</span></a></div>;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== t10 || $[37] !== t18) {
    t23 = <div>{t10}{t11}{t12}{t18}{t22}</div>;
    $[36] = t10;
    $[37] = t18;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="relative"><div className="relative flex items-center justify-center bg-transparent"><div className="relative aspect-[9/19.5] w-full max-w-sm rotate-[4deg] animate-[heroFloat_5s_ease-in-out_infinite] bg-transparent motion-reduce:animate-none"><Image src="/about/home.webp" alt="Factiii Platform Demo" fill={true} priority={true} loading="eager" fetchPriority="high" sizes="(max-width: 432px) calc(100vw - 3rem), 384px" className="!bg-transparent object-contain" style={{
            backgroundColor: "transparent"
          }} /></div></div><div className="pointer-events-none absolute -right-8 -top-8 size-32 rounded-full bg-primary/20 blur-3xl" /><div className="pointer-events-none absolute -bottom-8 -left-8 size-32 rounded-full bg-primary/20 blur-3xl" /></div>;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] !== t23) {
    t25 = <section className="relative overflow-hidden bg-background-primary px-6 py-20 pt-24 md:py-32">{t9}<div className="relative mx-auto max-w-7xl"><div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">{t23}{t24}</div></div></section>;
    $[40] = t23;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="mb-4 flex justify-center"><div className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-primary"><Handshake className="mr-2 size-3" /><h2 id="partners-heading" className="text-xs font-semibold leading-none">From the Team Behind</h2></div></div>;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      href: "https://taptrack.io/",
      logo: <Clock className="size-8" />,
      name: "Taptrack"
    };
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <section className="relative bg-background-secondary py-8" aria-labelledby="partners-heading"><div className="mx-auto">{t26}<PartnersCarousel partners={[t27, {
          href: "https://link3d.io/",
          logo: <LinkIcon className="size-8" />,
          name: "Link3D"
        }, {
          href: "https://greasemoto.com/",
          logo: "/images/greasemotoLogoWhite.png",
          name: "Greasemoto"
        }]} /></div></section>;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  let t29;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="mb-16 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary"><CheckCircle className="mr-2 size-4" /><span className="text-sm font-semibold">Our Principles</span></div><h2 className="text-4xl font-semibold text-textPrimary md:text-5xl" data-animate={true}>Humanity • Privacy • Transparency</h2><p className="mx-auto mt-4 max-w-2xl text-xl text-textSecondary">Our north star is simple: protect people, protect their data, and make truth easy to find.</p></div>;
    $[45] = t29;
  } else {
    t29 = $[45];
  }
  let t30;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="group rounded-2xl bg-background-secondary p-8 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"><div className="mb-4 inline-flex size-14 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-white"><Heart className="size-7" /></div><h3 className="mb-3 text-xl font-semibold text-textPrimary">Humanity First</h3><p className="leading-relaxed text-textSecondary">Mental health over monetization. No manipulative dark patterns, no dopamine traps—use Factiii on your terms.</p></div>;
    $[46] = t30;
  } else {
    t30 = $[46];
  }
  let t31;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="group rounded-2xl bg-background-secondary p-8 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"><div className="mb-4 inline-flex size-14 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-white"><ShieldCheck className="size-7" /></div><h3 className="mb-3 text-xl font-semibold text-textPrimary">Privacy You Own</h3><p className="leading-relaxed text-textSecondary">You own your data. Local-first by default with explicit opt-in sharing. We never sell your data—ever.</p></div>;
    $[47] = t31;
  } else {
    t31 = $[47];
  }
  let t32;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <section className="bg-background-primary px-6 py-24"><div className="mx-auto max-w-7xl">{t29}<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">{t30}{t31}<div className="group rounded-2xl bg-background-secondary p-8 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"><div className="mb-4 inline-flex size-14 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-white"><Eye className="size-7" /></div><h3 className="mb-3 text-xl font-semibold text-textPrimary">Radical Transparency</h3><p className="leading-relaxed text-textSecondary">Clear provenance with citations, timestamps, and locations. Explanations for why content and ads appear to you.</p></div></div></div></section>;
    $[48] = t32;
  } else {
    t32 = $[48];
  }
  let t33;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <div className="mb-16 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary"><CheckCircle className="mr-2 size-4" /><span className="text-sm font-semibold">Benefits</span></div><h2 className="text-4xl font-semibold text-textPrimary md:text-5xl" data-animate={true}>Why Choose Factiii</h2><p className="mx-auto mt-4 max-w-2xl text-xl text-textSecondary">Everything you need to discover, verify, and share trustworthy information</p></div>;
    $[49] = t33;
  } else {
    t33 = $[49];
  }
  let t34;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <section id="features" className="bg-background-primary px-6 py-24"><div className="mx-auto max-w-7xl">{t33}<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">{benefits.map(_temp5)}</div></div></section>;
    $[50] = t34;
  } else {
    t34 = $[50];
  }
  let t35;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="mb-16 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary"><PlayCircle className="mr-2 size-4" /><span className="text-sm font-semibold">How It Works</span></div><h2 className="text-3xl font-semibold text-textPrimary sm:text-4xl md:text-5xl lg:text-6xl" data-animate={true}>Explore Our Features</h2><p className="mx-auto mt-6 max-w-3xl text-xl text-textSecondary">Discover the powerful features that make Factiii the ultimate platform for truth and privacy</p></div>;
    $[51] = t35;
  } else {
    t35 = $[51];
  }
  let t36;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <ChevronLeft className="size-6" />;
    $[52] = t36;
  } else {
    t36 = $[52];
  }
  let t37;
  if ($[53] !== goToPrevious) {
    t37 = <button onClick={goToPrevious} className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-all hover:scale-110 hover:bg-black/70 sm:left-0 sm:p-3" aria-label="Previous feature">{t36}</button>;
    $[53] = goToPrevious;
    $[54] = t37;
  } else {
    t37 = $[54];
  }
  let t38;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <ChevronRight className="size-6" />;
    $[55] = t38;
  } else {
    t38 = $[55];
  }
  let t39;
  if ($[56] !== goToNext) {
    t39 = <button onClick={goToNext} className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-all hover:scale-110 hover:bg-black/70 sm:right-0 sm:p-3" aria-label="Next feature">{t38}</button>;
    $[56] = goToNext;
    $[57] = t39;
  } else {
    t39 = $[57];
  }
  let t40;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = {
      scrollSnapType: "x mandatory",
      scrollPadding: "0 50%",
      scrollbarWidth: "none",
      msOverflowStyle: "none"
    };
    $[58] = t40;
  } else {
    t40 = $[58];
  }
  let t41;
  let t42;
  let t43;
  let t44;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <style>{"\n                div::-webkit-scrollbar {\n                  display: none;\n                }\n              "}</style>;
    t42 = <div className="w-1/2 shrink-0" aria-hidden="true" />;
    t43 = features.map(_temp6);
    t44 = <div className="w-1/2 shrink-0" aria-hidden="true" />;
    $[59] = t41;
    $[60] = t42;
    $[61] = t43;
    $[62] = t44;
  } else {
    t41 = $[59];
    t42 = $[60];
    t43 = $[61];
    t44 = $[62];
  }
  let t45;
  if ($[63] !== handleManualScroll) {
    t45 = <div ref={scrollContainerRef} className="flex gap-6 overflow-x-auto pb-4" style={t40} onScroll={handleManualScroll}>{t41}{t42}{t43}{t44}</div>;
    $[63] = handleManualScroll;
    $[64] = t45;
  } else {
    t45 = $[64];
  }
  let t46;
  if ($[65] !== t37 || $[66] !== t39 || $[67] !== t45) {
    t46 = <section className="bg-background-secondary px-6 py-24"><div className="mx-auto max-w-7xl">{t35}<div className="relative">{t37}{t39}{t45}</div></div></section>;
    $[65] = t37;
    $[66] = t39;
    $[67] = t45;
    $[68] = t46;
  } else {
    t46 = $[68];
  }
  let t47;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <div className="mb-20 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary backdrop-blur-sm"><Hourglass className="mr-2 size-4" /><span className="text-sm font-semibold">What's Next</span></div><h2 className="text-4xl font-semibold text-textPrimary md:text-5xl lg:text-6xl" data-animate={true}>Coming Soon</h2><p className="mx-auto mt-6 max-w-3xl text-xl text-textSecondary">Upcoming features in our to-do list!</p></div>;
    $[69] = t47;
  } else {
    t47 = $[69];
  }
  let t48;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <div className="absolute -right-8 -top-8 size-32 rounded-full bg-primary/20 transition-all duration-500 group-hover:scale-110" />;
    $[70] = t48;
  } else {
    t48 = $[70];
  }
  let t49;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <div className="group relative overflow-hidden rounded-2xl bg-background-primary p-8 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:shadow-xl">{t48}<div className="relative"><div className="mb-6 flex size-16 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Sparkles className="size-8" /></div><h3 className="mb-4 text-2xl font-semibold text-textPrimary">Advanced AI</h3><p className="leading-relaxed text-textSecondary">Message privately with AI, customize their behaviors, and build as many factiii characters as you want. Trons power every model — pick the best one for the job with no subscriptions and no lock-ins.</p></div></div>;
    $[71] = t49;
  } else {
    t49 = $[71];
  }
  let t50;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <div className="absolute -right-8 -top-8 size-32 rounded-full bg-primary/20 transition-all duration-500 group-hover:scale-110" />;
    $[72] = t50;
  } else {
    t50 = $[72];
  }
  let t51;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <div className="group relative overflow-hidden rounded-2xl bg-background-primary p-8 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:shadow-xl">{t50}<div className="relative"><div className="mb-6 flex size-16 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Globe className="size-8" /></div><h3 className="mb-4 text-2xl font-semibold text-textPrimary">Post Once, Share Everywhere</h3><p className="leading-relaxed text-textSecondary">Publish a single post from Factiii and we send it everywhere — Reddit, YouTube, TikTok and more — through our API integrations, with new networks added as we go.</p></div></div>;
    $[73] = t51;
  } else {
    t51 = $[73];
  }
  let t52;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <div className="absolute -right-8 -top-8 size-32 rounded-full bg-primary/20 transition-all duration-500 group-hover:scale-110" />;
    $[74] = t52;
  } else {
    t52 = $[74];
  }
  let t53;
  let t54;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <section className="relative bg-background-secondary px-6 py-24"><div className="relative mx-auto max-w-7xl">{t47}<div className="grid gap-8 md:grid-cols-3">{t49}{t51}<div className="group relative overflow-hidden rounded-2xl bg-background-primary p-8 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:shadow-xl">{t52}<div className="relative"><div className="mb-6 flex size-16 items-center justify-center rounded-2xl bg-primary/10 text-primary"><LinkIcon className="size-8" /></div><h3 className="mb-4 text-2xl font-semibold text-textPrimary">Connected Workspaces</h3><p className="leading-relaxed text-textSecondary">Plug your Atlassian boards into AI to plan and ship code, and sync files straight from OneDrive. More integrations land as we build them out.</p></div></div></div></div></section>;
    t54 = <FeatureRequests />;
    $[75] = t53;
    $[76] = t54;
  } else {
    t53 = $[75];
    t54 = $[76];
  }
  let t55;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <div className="mb-16 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary"><Target className="mr-2 size-4" /><span className="text-sm font-semibold">Pricing</span></div><h2 className="text-3xl font-semibold text-textPrimary sm:text-4xl md:text-5xl">Simple, Transparent Pricing</h2><p className="mx-auto mt-4 max-w-2xl text-xl text-textSecondary">Factiii is free to use. Trons are your AI credits — they power AI features, and you can also give them as awards on posts you love (the trons go straight to the author). Our pricing is open math: one tron is one millionth of a dollar of what the AI costs us, and $1 buys a million of them, the same for everyone. Premium members spend trons at exactly our cost; everyone else spends a small margin more. No surveillance advertising, no dark patterns, no data sales.</p></div>;
    $[77] = t55;
  } else {
    t55 = $[77];
  }
  let t56;
  if ($[78] !== leftTron || $[79] !== router) {
    t56 = leftTron && <div className="rounded-2xl bg-background-secondary p-8 shadow-lg"><div className="mb-2 flex items-center gap-3"><ProductImage imageKey={leftTron.image} alt={leftTron.title || "Tron package"} size="md" round={true} /><h3 className="text-2xl font-semibold text-textPrimary">{leftTron.title}</h3></div><div className="mb-2 flex flex-wrap items-baseline gap-x-3"><span className="text-5xl font-semibold text-textPrimary">${(leftTron.displayPrice / 100).toFixed(2)}</span>{leftTron.displayPrice < leftTron.price && <span className="text-2xl text-textSecondary line-through">${(leftTron.price / 100).toFixed(2)}</span>}<span className="text-textSecondary"> one-time</span></div>{leftTron.tierPricingLabel && <div className="mb-6 inline-flex items-center rounded-full bg-emerald-500/10 px-3 py-1 text-sm font-semibold text-emerald-500">{leftTron.tierPricingLabel}</div>}<Button onClick={() => router.push("/products")} variant="SECONDARY" text="View Details" classNames="w-full mb-6" /><ul className="space-y-4"><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">{Number(leftTron.tron?.quantity || 0).toLocaleString()}{" "}trons</span></li>{(leftTron.tron?.premiumDays ?? 0) > 0 && <li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">{leftTron.tron?.premiumDays} days premium access</span></li>}<li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">Trons power AI features</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">Priority support</span></li></ul></div>;
    $[78] = leftTron;
    $[79] = router;
    $[80] = t56;
  } else {
    t56 = $[80];
  }
  let t57;
  if ($[81] !== currentUser || $[82] !== foundersModalOpen || $[83] !== foundersToken || $[84] !== openAuthContinueModal || $[85] !== router) {
    t57 = foundersToken && <div className="relative rounded-2xl bg-primary p-8 shadow-2xl"><div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-white px-4 py-1 text-sm font-semibold text-indigo-600">Limited Edition</div><div className="mb-2 flex items-center gap-3"><ProductImage imageKey={foundersToken.image} alt={foundersToken.title || "Founders Token"} size="md" /><h3 className="text-2xl font-semibold text-white">{foundersToken.title}</h3></div><div className="mb-2 flex flex-wrap items-baseline gap-x-3"><span className="text-5xl font-semibold text-white">${(foundersToken.displayPrice / 100).toFixed(0)}</span>{foundersToken.displayPrice < foundersToken.price && <span className="text-2xl text-white/60 line-through">${(foundersToken.price / 100).toFixed(0)}</span>}<span className="text-white/80"> one-time</span></div>{foundersToken.displayPrice < foundersToken.price && <div className="mb-2 inline-flex items-center rounded-full bg-white px-3 py-1 text-sm font-bold text-indigo-600">{Math.round((1 - foundersToken.displayPrice / foundersToken.price) * 100)}% off — founding wave pricing</div>}<p className="mb-6 text-sm text-white/80">3,000 available at this price — only 10,000 Founders Tokens will ever exist. The price rises when this wave sells out.</p><Button onClick={() => {
        if (!currentUser.isAuthenticated) {
          openAuthContinueModal();
          return;
        }
        if (foundersToken.owned) {
          router.push("/products");
          return;
        }
        setFoundersModalOpen(true);
      }} variant="SECONDARY" text="Secure Yours" classNames="w-full hover:bg-primary mb-6 border-2 border-white shadow-lg font-semibold" />{foundersModalOpen && <PurchaseModal product={foundersToken} closeHandler={() => setFoundersModalOpen(false)} />}<ul className="space-y-4"><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-white" /><span className="text-white">{Number(foundersToken.tron?.quantity || 0).toLocaleString()}{" "}trons</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-white" /><span className="text-white">Lifetime premium access (100-year subscription)</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-white" /><span className="text-white">Exclusive Founders space</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-white" /><span className="text-white">Spend every tron at exact AI cost, forever</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-white" /><span className="text-white">Trons power AI features</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-white" /><span className="text-white">Only {foundersToken.inventory.toLocaleString()} left at this price</span></li></ul></div>;
    $[81] = currentUser;
    $[82] = foundersModalOpen;
    $[83] = foundersToken;
    $[84] = openAuthContinueModal;
    $[85] = router;
    $[86] = t57;
  } else {
    t57 = $[86];
  }
  let t58;
  if ($[87] !== rightTron || $[88] !== router) {
    t58 = rightTron && <div className="rounded-2xl bg-background-secondary p-8 shadow-lg"><div className="mb-2 flex items-center gap-3"><ProductImage imageKey={rightTron.image} alt={rightTron.title || "Tron package"} size="md" round={true} /><h3 className="text-2xl font-semibold text-textPrimary">{rightTron.title}</h3></div><div className="mb-2 flex flex-wrap items-baseline gap-x-3"><span className="text-5xl font-semibold text-textPrimary">${(rightTron.displayPrice / 100).toFixed(2)}</span>{rightTron.displayPrice < rightTron.price && <span className="text-2xl text-textSecondary line-through">${(rightTron.price / 100).toFixed(2)}</span>}<span className="text-textSecondary"> one-time</span></div>{rightTron.tierPricingLabel && <div className="mb-6 inline-flex items-center rounded-full bg-emerald-500/10 px-3 py-1 text-sm font-semibold text-emerald-500">{rightTron.tierPricingLabel}</div>}<Button onClick={() => router.push("/products")} variant="SECONDARY" text="View Details" classNames="w-full mb-6" /><ul className="space-y-4"><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">{Number(rightTron.tron?.quantity || 0).toLocaleString()}{" "}trons</span></li>{(rightTron.tron?.premiumDays ?? 0) > 0 && <li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">{rightTron.tron?.premiumDays} days premium access</span></li>}<li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">Trons power AI features</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">Priority support</span></li></ul></div>;
    $[87] = rightTron;
    $[88] = router;
    $[89] = t58;
  } else {
    t58 = $[89];
  }
  let t59;
  if ($[90] !== t56 || $[91] !== t57 || $[92] !== t58) {
    t59 = <section id="pricing" className="bg-background-primary px-6 py-24"><div className="mx-auto max-w-7xl">{t55}<div className="grid gap-6 md:gap-8 lg:grid-cols-3">{t56}{t57}{t58}</div></div></section>;
    $[90] = t56;
    $[91] = t57;
    $[92] = t58;
    $[93] = t59;
  } else {
    t59 = $[93];
  }
  let t60;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <div className="mb-16 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary"><Sparkles className="mr-2 size-4" /><span className="text-sm font-semibold">Our Vision</span></div><h2 className="text-3xl font-semibold text-textPrimary sm:text-4xl md:text-5xl" data-animate={true}>Why We're Building Factiii</h2><p className="mx-auto mt-4 max-w-2xl text-xl text-textSecondary">The principles that drive every decision we make</p></div>;
    $[94] = t60;
  } else {
    t60 = $[94];
  }
  let t61;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <section className="bg-background-secondary px-6 py-24"><div className="mx-auto max-w-7xl">{t60}<div className="grid gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-3">{teamVisions.map(_temp7)}</div></div></section>;
    $[95] = t61;
  } else {
    t61 = $[95];
  }
  let t62;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = <div className="mb-16 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary"><FileText className="mr-2 size-4" /><span className="text-sm font-semibold">FAQ</span></div><h2 className="text-3xl font-semibold text-textPrimary sm:text-4xl md:text-5xl">Frequently Asked Questions</h2><p className="mx-auto mt-4 max-w-2xl text-xl text-textSecondary">Get answers to common questions about Factiii</p></div>;
    $[96] = t62;
  } else {
    t62 = $[96];
  }
  let t63;
  if ($[97] !== openFaqId) {
    t63 = faqs.map(faq => <div key={faq.id} className="overflow-hidden rounded-xl bg-background-secondary shadow-lg"><button onClick={() => setOpenFaqId(openFaqId === faq.id ? null : faq.id)} aria-expanded={openFaqId === faq.id} aria-controls={`faq-answer-${faq.id}`} className="flex w-full items-center justify-between p-6 text-left transition-colors hover:bg-background-light"><h3 className="text-lg font-semibold text-textPrimary">{faq.question}</h3><ChevronDown className={`size-5 shrink-0 text-primary transition-transform duration-300 ${openFaqId === faq.id ? "rotate-180" : ""}`} /></button><div id={`faq-answer-${faq.id}`} role="region" className={`grid transition-[grid-template-rows,opacity] duration-300 ${openFaqId === faq.id ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}><div className="overflow-hidden"><div className="px-6 pb-6"><p className="leading-relaxed text-textSecondary">{faq.answer}</p></div></div></div></div>);
    $[97] = openFaqId;
    $[98] = t63;
  } else {
    t63 = $[98];
  }
  let t64;
  if ($[99] !== t63) {
    t64 = <section className="bg-background-primary px-6 py-24"><div className="mx-auto max-w-4xl">{t62}<div className="space-y-4">{t63}</div></div></section>;
    $[99] = t63;
    $[100] = t64;
  } else {
    t64 = $[100];
  }
  const t65 = "contact";
  const t66 = "relative bg-primary px-6 py-32";
  const t67 = "relative mx-auto max-w-5xl text-center";
  let t68;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <h2 className="text-4xl font-semibold text-white md:text-5xl lg:text-6xl">Ready to Join the Future of Truth?</h2>;
    $[101] = t68;
  } else {
    t68 = $[101];
  }
  const t69 = "mx-auto mt-6 max-w-2xl text-xl text-white/90";
  const t70 = "Join ";
  const t71 = stats.users.toLocaleString();
  let t72;
  if ($[102] !== t71) {
    t72 = <p className={t69}>{t70}{t71}+ community members already using Factiii to discover, verify, and share trustworthy information.</p>;
    $[102] = t71;
    $[103] = t72;
  } else {
    t72 = $[103];
  }
  let t73;
  if ($[104] !== currentUser?.isAuthenticated || $[105] !== openSignupModal || $[106] !== router) {
    t73 = currentUser?.isAuthenticated ? () => router.push("/home") : openSignupModal;
    $[104] = currentUser?.isAuthenticated;
    $[105] = openSignupModal;
    $[106] = router;
    $[107] = t73;
  } else {
    t73 = $[107];
  }
  const t74 = currentUser?.isAuthenticated ? "Go to App" : "Join the Community";
  let t75;
  if ($[108] !== t73 || $[109] !== t74) {
    t75 = <Button onClick={t73} variant="PRIMARY" text={t74} classNames="px-12 py-6 text-lg font-semibold shadow-2xl transform transition-all duration-300 w-72 border-2 border-white hover:!bg-white hover:!text-primary hover:scale-105" />;
    $[108] = t73;
    $[109] = t74;
    $[110] = t75;
  } else {
    t75 = $[110];
  }
  let t76;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t76 = <Button onClick={() => setShowContactModal(true)} variant="SECONDARY" text="Contact Us" classNames="border-2 border-white/50 bg-white/10 px-12 py-6 text-lg font-semibold text-white backdrop-blur-sm hover:bg-white hover:text-primary transform hover:scale-105 transition-all duration-300 w-72" />;
    $[111] = t76;
  } else {
    t76 = $[111];
  }
  let t77;
  if ($[112] !== t75) {
    t77 = <div className="mt-12 flex flex-col items-center justify-center gap-6 sm:flex-row">{t75}{t76}</div>;
    $[112] = t75;
    $[113] = t77;
  } else {
    t77 = $[113];
  }
  let t78;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t78 = <p className="mt-8 text-sm text-white/80">Join the community • Share your truth • Connect with others</p>;
    $[114] = t78;
  } else {
    t78 = $[114];
  }
  let t79;
  if ($[115] !== t68 || $[116] !== t72 || $[117] !== t77) {
    t79 = <section id={t65} className={t66}><div className={t67}><div>{t68}{t72}{t77}{t78}</div></div></section>;
    $[115] = t68;
    $[116] = t72;
    $[117] = t77;
    $[118] = t79;
  } else {
    t79 = $[118];
  }
  let t80;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t80 = <MarketingFooter />;
    $[119] = t80;
  } else {
    t80 = $[119];
  }
  let t81;
  if ($[120] !== showContactModal) {
    t81 = showContactModal && <ContactFormModal onClose={() => setShowContactModal(false)} />;
    $[120] = showContactModal;
    $[121] = t81;
  } else {
    t81 = $[121];
  }
  let t82;
  if ($[122] !== desktopDownloadOpen) {
    t82 = desktopDownloadOpen && <DesktopDownloadModal closeHandler={() => setDesktopDownloadOpen(false)} />;
    $[122] = desktopDownloadOpen;
    $[123] = t82;
  } else {
    t82 = $[123];
  }
  let t83;
  if ($[124] !== exploreSlot || $[125] !== t25 || $[126] !== t28 || $[127] !== t32 || $[128] !== t34 || $[129] !== t46 || $[130] !== t53 || $[131] !== t54 || $[132] !== t59 || $[133] !== t61 || $[134] !== t64 || $[135] !== t7 || $[136] !== t79 || $[137] !== t8 || $[138] !== t81 || $[139] !== t82) {
    t83 = <>{t7}{t8}{t25}{t28}{t32}{t34}{t46}{t53}{t54}{t59}{t61}{t64}{t79}{exploreSlot}{t80}{t81}{t82}</>;
    $[124] = exploreSlot;
    $[125] = t25;
    $[126] = t28;
    $[127] = t32;
    $[128] = t34;
    $[129] = t46;
    $[130] = t53;
    $[131] = t54;
    $[132] = t59;
    $[133] = t61;
    $[134] = t64;
    $[135] = t7;
    $[136] = t79;
    $[137] = t8;
    $[138] = t81;
    $[139] = t82;
    $[140] = t83;
  } else {
    t83 = $[140];
  }
  return t83;
};
function _temp(product) {
  return product.type === "TRON";
}
function _temp2(product_0) {
  return product_0.type === "FOUNDERS_TOKEN";
}
function _temp3(product_1) {
  return product_1.purchasable;
}
function _temp4(a, b) {
  return a.price - b.price;
}
function _temp5(benefit) {
  return <div key={benefit.id} className="group rounded-2xl bg-background-secondary p-8 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"><div className="mb-4 inline-flex size-14 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-white"><benefit.icon className="size-7" /></div><h3 className="mb-3 text-xl font-semibold text-textPrimary">{benefit.title}</h3><p className="leading-relaxed text-textSecondary">{benefit.description}</p></div>;
}
function _temp6(feature) {
  return <div key={feature.id} data-feature-card="true" className="relative min-w-[90%] sm:min-w-[400px] md:min-w-[450px]" style={{
    scrollSnapAlign: "center"
  }}><div className="overflow-hidden rounded-2xl bg-background-secondary shadow-lg transition-all duration-300 hover:shadow-xl"><FeatureImage src={feature.videoSrc} alt={feature.title} /><div className="p-6"><div className="mb-3 flex items-center space-x-3"><div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><feature.icon className="size-5" /></div><h3 className="text-xl font-semibold text-textPrimary">{feature.title}</h3></div><p className="text-sm leading-relaxed text-textSecondary">{feature.description}</p></div></div></div>;
}
function _temp7(vision) {
  return <div key={vision.id} className="group rounded-2xl bg-background-primary p-8 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"><div className="mb-4 inline-flex size-14 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-white"><vision.icon className="size-7" /></div><p className="mb-6 text-lg leading-relaxed text-textSecondary">"{vision.content}"</p><div className="flex items-center"><div className="ml-0"><p className="font-semibold text-textPrimary">{vision.name}</p><p className="text-sm text-primary">{vision.role}</p></div></div></div>;
}